import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Table, Modal, Tooltip} from 'antd';
import {
  FilterOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {CSVLink} from 'react-csv';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import dayjs from 'dayjs';

import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import ReceiptComponent from '../../CommonComponents/receiptComponent';
import {
  PageSizeArray,
  PaginationPosition,
  DefaultPaginationValues,
  registrationHistoryFilters,
  registrationFilterOptions,
  formatCurrencyByLocalChapter,
  getStatusInfoOfEventRegistration,
  calculateProcessingFeeForRegistrations,
  hasProcessingFeeExplicitlyDisabled,
  ReturnNAOnEmpty,
} from '../../../Util/commonUtility';
import useLocalization from '../../../Localization';
import {getAllChapters} from '../../../Store/Actions';
import {getAllEventsByQueryStringSearch} from '../../../Store/Actions/events';
import {
  getRegistrationHistory,
  getReceiptByTransactionId,
} from '../../../Store/Actions/registrationHistory';

const statusLabel = (value) =>
  registrationFilterOptions.find((o) => `${o.value}` === `${value}`)?.label ||
  'N/A';

// Maps the DynamicSearch form values to the API's registration-history query params.
const buildApiFilters = (values) => {
  if (!values) return {};
  const filters = {};
  if (values.attendee_email)
    filters.attendee_email = values.attendee_email.trim();
  if (values.member_email) filters.member_email = values.member_email.trim();
  if (values.attendee_first_name)
    filters.attendee_first_name = values.attendee_first_name.trim();
  if (values.attendee_last_name)
    filters.attendee_last_name = values.attendee_last_name.trim();
  if (values.attendee_company)
    filters.attendee_company = values.attendee_company.trim();
  if (values.discount_code) filters.discount_code = values.discount_code.trim();
  if (values.transaction_id)
    filters.transaction_id = values.transaction_id.trim();
  if (values.registration_id)
    filters.registration_id = `${values.registration_id}`.trim();
  const registrationType = Array.isArray(values.registration_type)
    ? values.registration_type[0]
    : values.registration_type;
  if (registrationType) filters.registration_type = registrationType;
  if (values.event_id) {
    filters.event_id = Array.isArray(values.event_id)
      ? values.event_id[0]
      : values.event_id;
  }
  const chapter = Array.isArray(values.local_chapter_id)
    ? values.local_chapter_id[0]
    : values.local_chapter_id;
  if (chapter) filters.chapter_id = chapter;
  const status = Array.isArray(values.status)
    ? values.status[0]
    : values.status;
  if (status !== undefined && status !== '' && status !== null) {
    filters.status = status;
  }
  // The dateRange field arrives either as formatted strings (via onFinish) or as
  // raw dayjs objects (e.g. the reset path calls onSearch without formatting).
  // Normalize both to YYYY-MM-DD so the API never receives an unparseable value.
  if (Array.isArray(values.date) && values.date[0] && values.date[1]) {
    const from = dayjs(values.date[0]);
    const to = dayjs(values.date[1]);
    if (from.isValid() && to.isValid()) {
      filters.created_from = from.format('YYYY-MM-DD');
      filters.created_to = to.format('YYYY-MM-DD');
    }
  }
  return filters;
};

const toQueryString = (obj) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

// Processing-fee helpers mirror the Event Registration admin table so the
// "Paid (Includes Processing Fee)" column reconciles across both screens.
const getProcessingFee = (record) => {
  const paidAmount = Number(record?.paid_amount || 0);
  if (paidAmount <= 0) return 0;
  if (hasProcessingFeeExplicitlyDisabled(record)) return 0;

  const transactionProcessingFee = Number(record?.processing_fee_amount || 0);
  const transactionBaseAmount = Number(record?.payment_base_amount || 0);
  if (transactionProcessingFee > 0 && transactionBaseAmount > 0) {
    return (
      Math.round(
        (paidAmount / transactionBaseAmount) * transactionProcessingFee * 100,
      ) / 100
    );
  }

  const recordWithoutTransactionFee = {
    ...record,
    processing_fee_amount: undefined,
    processing_fee: undefined,
    payment_processing_fee: undefined,
    transaction_fee: undefined,
    card_processing_fee: undefined,
  };

  return calculateProcessingFeeForRegistrations(
    [
      {
        ...recordWithoutTransactionFee,
        total_cost: paidAmount,
        discount_amount: 0,
      },
    ],
    recordWithoutTransactionFee,
  );
};

const getPaidAmountWithProcessingFee = (record) => {
  const paidAmount = Number(record?.paid_amount || 0);
  if (paidAmount <= 0) return 0;
  return Math.round((paidAmount + getProcessingFee(record)) * 100) / 100;
};

const attendeeTypeLabel = (record) => {
  if (record?.is_reps) return 'Vendor Reps';
  if (record?.is_sponsor)
    return record?.event_registration_options?.sponsorship?.name || 'Sponsor';
  return 'Regular';
};

const registrationTypeLabel = (text) => {
  const typeEnum = {
    in_person: 'In Person',
    webcast: 'Webcast',
    Workshop: 'Workshop',
  };
  return typeEnum[text] || text || 'N/A';
};

const getRefundMethod = (record) => {
  if (record?.refunded_amount > 0) {
    return record?.refund_to_account
      ? 'Refund in PBSS Credit'
      : 'Refund in Bank Account';
  }
  return 'N/A';
};

// A plain dollar prefix keeps Excel from dropping trailing cents (2047 → 2047, 53.30 → 53.3).
const csvAmount = (value) => `$${Number(value || 0).toFixed(2)}`;

// Maps one registration record into a flat CSV row. Shared by the CSV export.
const mapRegistrationToCSVRow = (r) => ({
  RegistrationId: r.id,
  EventId: r.event_id ?? 'N/A',
  Event: r.event_name || 'N/A',
  EventDate: r.event_start_date_time
    ? dayjs(r.event_start_date_time).format('YYYY-MM-DD hh:mm A')
    : 'N/A',
  Chapter: r.chapter_name || 'N/A',
  MemberName: `${r.member_first_name || ''} ${r.member_last_name || ''}`.trim(),
  MemberEmail: r.member_email || 'N/A',
  AttendeeFirstName: r.attendee_first_name || '',
  AttendeeLastName: r.attendee_last_name || '',
  AttendeeName: `${r.attendee_first_name || ''} ${
    r.attendee_last_name || ''
  }`.trim(),
  AttendeeEmail: r.attendee_email || 'N/A',
  Company: r.attendee_company || 'N/A',
  AttendeeType: attendeeTypeLabel(r),
  RegistrationType: registrationTypeLabel(r.registration_type),
  RegistrationOption:
    r.event_registration_options?.sponsorship?.name ||
    r.event_registration_options?.registration?.name ||
    'N/A',
  Food: r.event_registration_options?.food?.name || 'No Food',
  Status: statusLabel(r.status),
  CouponCode: r.discount_code || 'N/A',
  TotalCost: csvAmount(r.total_cost),
  DiscountAmount: csvAmount(r.discount_amount),
  PaidAmount: csvAmount(getPaidAmountWithProcessingFee(r)),
  ProcessingFee: csvAmount(getProcessingFee(r)),
  CreditAmount: csvAmount(r.credit_amount),
  RefundedAmount: csvAmount(r.refunded_amount),
  RefundMethod: getRefundMethod(r),
  StripePaymentId: r.payment_id || r.transaction_id || 'N/A',
  CreditCardNumber: r.card_last4 ? `**** **** **** ${r.card_last4}` : 'N/A',
  StripePaymentDate: r.payment_date || 'N/A',
  BillingCountry: r.payment_country || 'N/A',
  BillingZip: r.payment_zip || 'N/A',
  Note: r.note || 'N/A',
  CreatedAt: r.created_at
    ? dayjs(r.created_at).format('YYYY-MM-DD hh:mm A')
    : 'N/A',
});

const RegistrationHistoryComponent = () => {
  const dispatch = useDispatch();
  const {ApplicationString} = useLocalization();
  const [searchParams] = useSearchParams();
  const presetUserId = searchParams.get('user_id');

  const allChapters = useSelector((state) => state.chapters.allChapters);
  const allEventsQueryData = useSelector(
    ({events}) => events.allEventsQueryData,
  );

  // Event dropdown filter (searchable by event id or name) — mirrors CouponUsersTrack.
  const [eventsList, setEventsList] = useState([]);
  const [fetching, setFetching] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [searchValues, setSearchValues] = useState(null);
  const [pagination, setPagination] = useState({
    page: DefaultPaginationValues.PAGE,
    pageCount: DefaultPaginationValues.PAGE_SIZE,
    totalCount: 0,
  });
  const [sortParams, setSortParams] = useState({
    field: 'created_at',
    order: 'descend',
  });
  const [loading, setLoading] = useState(false);

  // CSV export mirrors the Member/Attendees (Users) flow: one "Load CSV Data"
  // request pulls the full result set (page 1, pageCount = total), then a
  // react-csv link downloads it. csvRows holds the mapped, ready-to-write rows.
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvRows, setCsvRows] = useState([]);

  // Hidden receipt used for direct (no-modal) download on the receipt icon.
  const receiptRef = useRef(null);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptChapterData, setReceiptChapterData] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(false);

  const chapterList = useMemo(
    () => allChapters?.data?.chapters || [],
    [allChapters],
  );
  const chapterFor = (chapterId) =>
    chapterList.find((c) => c.id === Number(chapterId));
  const chaptersOptions = useMemo(
    () => chapterList.map((c) => ({value: c.id, label: c.name})),
    [chapterList],
  );

  const fetchRegistrations = (overrides = {}) => {
    const page = overrides.page ?? pagination.page;
    const pageCount = overrides.pageCount ?? pagination.pageCount;
    const sortField = overrides.sortField ?? sortParams.field;
    const sortOrder = overrides.sortOrder ?? sortParams.order;
    const values = overrides.searchValues ?? searchValues;

    const apiFilters = buildApiFilters(values);
    if (presetUserId) apiFilters.user_id = presetUserId;
    const queryData = toQueryString(apiFilters);

    setLoading(true);
    dispatch(
      getRegistrationHistory(
        {page, pageCount},
        sortField,
        sortOrder,
        queryData,
        true, // hideLoader: the table shows its own loader
      ),
    ).then((data) => {
      setRegistrations(data?.registrations || []);
      setPagination({
        page: data?.currentPage || page,
        pageCount: data?.pageCount || pageCount,
        totalCount: data?.totalCount || 0,
      });
      setLoading(false);
    });
  };

  useEffect(() => {
    dispatch(getAllChapters(true)); // hideLoader: filter dropdown data only
    // hideLoader: this only populates the filter dropdown, so it must not stack
    // a second (global) spinner on top of the table's own loader.
    dispatch(
      getAllEventsByQueryStringSearch(
        {page: 1, pagecount: 100},
        '',
        '',
        '',
        true,
      ),
    );
    fetchRegistrations({page: 1});
  }, [presetUserId]);

  // Map the searchable events response into the dropdown's option list.
  useEffect(() => {
    if (allEventsQueryData?.forSearch) {
      const events = allEventsQueryData.forSearch?.data?.events || [];
      setEventsList(
        events.map((e) => ({
          value: e.id,
          label:
            e.id != null && e.name != null
              ? `${e.id} - ${e.name}`
              : e.name || String(e.id),
        })),
      );
      setFetching(false);
    }
  }, [allEventsQueryData?.forSearch]);

  // Once the hidden receipt has data + is ready, trigger the PDF download.
  useEffect(() => {
    if (!pendingDownload || !receiptData) return;
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < 25 && !cancelled; i += 1) {
        if (receiptRef.current?.isReady) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      if (cancelled) return;
      await receiptRef.current?.downloadPDF();
      if (!cancelled) setPendingDownload(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [pendingDownload, receiptData]);

  const handleSearch = (values) => {
    setSearchValues(values);
    setCsvRows([]); // filters changed — force a fresh CSV load before download
    fetchRegistrations({page: 1, searchValues: values});
  };

  const handleTableChange = (paging, _filters, sorter) => {
    // antd clears the sort on the third click of a column (order === undefined).
    // Fall back to the default created_at/descend sort so the indicator clears
    // instead of sticking on the same column.
    const order = sorter?.order || 'descend';
    const field = sorter?.order ? sorter?.field : 'created_at';
    setSortParams({field, order});
    setCsvRows([]); // sort/paging changed — invalidate any loaded CSV data
    setPagination((p) => ({
      ...p,
      page: paging.current,
      pageCount: paging.pageSize,
    }));
    fetchRegistrations({
      page: paging.current,
      pageCount: paging.pageSize,
      sortField: field,
      sortOrder: order,
    });
  };

  // Download the combined receipt (all attendees sharing this transaction) directly.
  const openReceipt = async (record) => {
    if (!record?.transaction_id) {
      Modal.info({
        title: 'Receipt unavailable',
        content:
          'This registration has no transaction id (no payment receipt).',
      });
      return;
    }
    const data = await dispatch(
      getReceiptByTransactionId(record.transaction_id),
    );
    if (!data) return;
    // Map transaction response into the shape ReceiptComponent expects (mirrors My Events).
    const eventData = {
      ...data,
      attendees: data?.eventRegistrations?.attendees || [],
      sponsors: data?.eventRegistrations?.attendeesWithPerks || [],
      payments: data?.payments || [],
    };
    // Reset first so the hidden receipt remounts and re-computes readiness.
    setReceiptData(null);
    setPendingDownload(false);
    setReceiptChapterData(chapterFor(data?.local_chapter_id));
    setTimeout(() => {
      setReceiptData(eventData);
      setPendingDownload(true);
    }, 0);
  };

  // Load the full filtered result set in a single request (page 1, pageCount =
  // total), exactly like the Member/Attendees (Users) CSV flow. Once loaded, the
  // react-csv "Download CSV" link writes every row — not just the current page.
  const handleLoadCSVData = async () => {
    if (csvLoading) return;

    const apiFilters = buildApiFilters(searchValues);
    if (presetUserId) apiFilters.user_id = presetUserId;
    const queryData = toQueryString(apiFilters);

    // Size the request to the full result count (fallback keeps it large if the
    // total isn't known yet), matching Users' `pageCount = totalUserCount`.
    const fullCount = pagination.totalCount || 100000;

    setCsvLoading(true);
    try {
      const data = await dispatch(
        getRegistrationHistory(
          {page: 1, pageCount: fullCount},
          sortParams.field,
          sortParams.order,
          queryData,
          true, // hideLoader: the "Fetching registrations.." button shows progress
        ),
      );
      const rows = (data?.registrations || []).map(mapRegistrationToCSVRow);
      if (!rows.length) {
        Modal.info({
          title: 'Nothing to export',
          content: 'There are no registrations matching the current filters.',
        });
      }
      setCsvRows(rows);
    } finally {
      setCsvLoading(false);
    }
  };

  // react-csv column headers, derived from the mapped row shape.
  const csvHeaders = useMemo(() => {
    const template = mapRegistrationToCSVRow({});
    return Object.keys(template).map((k) => ({label: k, key: k}));
  }, []);

  const money = (value, record) =>
    formatCurrencyByLocalChapter(
      value ? value : 0,
      chapterFor(record.chapter_id),
    );

  const columns = [
    {title: 'Reg. ID', dataIndex: 'id', sorter: true, width: 90},
    {
      title: 'Member Details',
      dataIndex: 'member_email',
      sorter: true,
      render: (_, r) => (
        <div>
          <div>
            {`${r.member_first_name || ''} ${
              r.member_last_name || ''
            }`.trim() || 'N/A'}
          </div>
          <div style={{fontSize: 12, color: '#888'}}>
            {r.member_email || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Attendee Details',
      dataIndex: 'attendee_email',
      sorter: true,
      render: (_, r) => (
        <div>
          <div>
            {`${r.attendee_first_name || ''} ${
              r.attendee_last_name || ''
            }`.trim() || 'N/A'}
          </div>
          <div style={{fontSize: 12, color: '#888'}}>
            {r.attendee_email || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'attendee_company',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Event ID',
      dataIndex: 'event_id',
      sorter: true,
      width: 100,
      render: (t) => (t != null ? t : 'N/A'),
    },
    {
      title: 'Event',
      dataIndex: 'event_name',
      sorter: true,
      render: (t) => t || 'N/A',
    },
    {
      title: 'Event Date',
      dataIndex: 'event_start_date_time',
      sorter: true,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD hh:mm A') : 'N/A'),
    },
    {
      title: 'Chapter',
      dataIndex: 'chapter_name',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Attendee Type',
      key: 'attendee_type',
      render: (_, r) => attendeeTypeLabel(r),
    },
    {
      title: 'Registration Type',
      dataIndex: 'registration_type',
      render: (t) => registrationTypeLabel(t),
    },
    {
      title: 'Registration Option',
      key: 'registration_option',
      render: (_, r) => {
        const sponsorship = r.event_registration_options?.sponsorship?.name;
        const registration = r.event_registration_options?.registration;
        if (r.is_reps || r.is_sponsor) {
          return sponsorship || 'N/A';
        }
        if (!registration?.name) return 'N/A';
        return `${registration.name} - ${formatCurrencyByLocalChapter(
          registration.price || 0,
          chapterFor(r.chapter_id),
        )}`;
      },
    },
    {
      title: 'Food Option',
      key: 'food_option',
      render: (_, r) => {
        const food = r.event_registration_options?.food;
        return food?.name
          ? `${food.name} - ${formatCurrencyByLocalChapter(
              food.price || 0,
              chapterFor(r.chapter_id),
            )}`
          : 'No Food';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      render: (status) => {
        const {color, label} = getStatusInfoOfEventRegistration(
          status,
          ApplicationString,
        );
        return <span style={{color}}>{label}</span>;
      },
    },
    {
      title: 'Coupon Code',
      dataIndex: 'discount_code',
      render: (t) => ReturnNAOnEmpty(t),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      sorter: true,
      render: (v, r) => money(v, r),
    },
    {
      title: 'Discounted Amount',
      dataIndex: 'discount_amount',
      render: (v, r) => money(v, r),
    },
    {
      title: 'Paid Amount (Includes Processing Fee)',
      dataIndex: 'paid_amount',
      sorter: true,
      render: (_, r) => {
        const processingFee = getProcessingFee(r);
        const paidWithFee = getPaidAmountWithProcessingFee(r);
        return (
          <Tooltip
            color='#fff'
            overlayInnerStyle={{color: '#111'}}
            title={
              processingFee > 0
                ? `Processing fee added: ${formatCurrencyByLocalChapter(
                    processingFee,
                    chapterFor(r.chapter_id),
                    2,
                    2,
                  )}`
                : 'No processing fee added.'
            }>
            {formatCurrencyByLocalChapter(
              paidWithFee,
              chapterFor(r.chapter_id),
              2,
              2,
            )}
          </Tooltip>
        );
      },
    },
    {
      title: 'Credit Amount',
      dataIndex: 'credit_amount',
      render: (v, r) => money(v, r),
    },
    {
      title: 'Refunded Amount',
      dataIndex: 'refunded_amount',
      sorter: true,
      render: (v, r) => money(v, r),
    },
    {
      title: 'Refund Method',
      key: 'refund_method',
      render: (_, r) => getRefundMethod(r),
    },
    {
      title: 'Stripe Payment ID',
      dataIndex: 'payment_id',
      render: (t, r) => t || r.transaction_id || 'N/A',
    },
    {
      title: 'Credit Card Number',
      dataIndex: 'card_last4',
      render: (t) => (t ? `**** **** **** ${t}` : 'N/A'),
    },
    {
      title: 'Stripe Payment Date',
      dataIndex: 'payment_date',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Billing Country',
      dataIndex: 'payment_country',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Billing ZIP',
      dataIndex: 'payment_zip',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      render: (t) => t || 'N/A',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      sorter: true,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD hh:mm A') : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 90,
      render: (_, record) => (
        <Tooltip title='Download Receipt'>
          <Button
            type='text'
            icon={<DownloadOutlined />}
            onClick={() => openReceipt(record)}
            id={`registrationHistory-receipt-${record.id}`}
          />
        </Tooltip>
      ),
    },
  ];

  columns.forEach((c) => {
    if (c.dataIndex === sortParams.field) c.sortOrder = sortParams.order;
  });

  return (
    <div className='common-container'>
      <div className='search-container' style={{gap: '10px'}}>
        <Button
          onClick={() => setExpanded((e) => !e)}
          id='registrationHistory-filter'>
          <FilterOutlined /> Filters
        </Button>
        {csvLoading && (
          <Button icon={<FileTextOutlined />} loading>
            Fetching registrations..
          </Button>
        )}
        {!csvLoading && !csvRows.length && (
          <Button
            icon={<FileTextOutlined />}
            onClick={handleLoadCSVData}
            id='registrationHistory-load-csv'>
            Load CSV Data
          </Button>
        )}
        {!csvLoading && csvRows.length > 0 && (
          <Button
            icon={<DownloadOutlined />}
            id='registrationHistory-download-csv'>
            <CSVLink
              data={csvRows}
              headers={csvHeaders}
              filename={'registration-history.csv'}
              style={{color: 'inherit'}}>
              Download CSV ({csvRows.length.toLocaleString()})
            </CSVLink>
          </Button>
        )}
      </div>

      {expanded && (
        <DynamicSearchForm
          columns={registrationHistoryFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'registration_history'}
          eventsList={eventsList}
          setEventsList={setEventsList}
          fetching={fetching}
          setFetching={setFetching}
          eventIdOptions={eventsList}
          chaptersOptions={chaptersOptions}
        />
      )}

      <div className='listing-container'>
        <Table
          bordered
          className='admin-table'
          columns={columns}
          dataSource={registrations}
          rowKey={'id'}
          loading={loading}
          onChange={handleTableChange}
          sticky={true}
          scroll={{x: 2800}}
          pagination={{
            current: pagination.page,
            total: pagination.totalCount,
            pageSize: pagination.pageCount,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
            position: [PaginationPosition],
          }}
        />
      </div>

      {/* Hidden off-screen receipt used only to generate the downloadable PDF. */}
      <div
        aria-hidden='true'
        style={{
          position: 'absolute',
          left: -9999,
          top: 0,
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}>
        {receiptData && (
          <ReceiptComponent
            ref={receiptRef}
            eventRegistrationType={'registration'}
            eventData={receiptData}
            eventLocalChapterData={receiptChapterData}
            hideDownloadButton
            showTransactionKey
          />
        )}
      </div>
    </div>
  );
};

export default RegistrationHistoryComponent;
